import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spin, message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { EAIChatPageId } from 'types/ai';
import useLoadBasicData from 'hooks/useLoadBasicData';
import usePPAOnboardingReviewInfo from 'hooks/usePPAOnboardingReviewInfo';
import useGetPPAServiceDetail from 'hooks/useGetPPAServiceDetail';
import { submitInvitedProviderReview, saveStep, getStep } from 'api/ppaOnboarding';
import WarningTips from 'components/WarningTips';
import ProfileCard from 'pages/PPAOnboardingReviewPage/components/ProfileCard';
import ServicesCard from 'pages/PPAOnboardingReviewPage/components/ServicesCard';
import PersonalIndentification from 'pages/PPAOnboardingReviewPage/components/PersonalIndentification';
import MedicalLicenses from 'pages/PPAOnboardingReviewPage/components/MedicalLicenses';
import WorkingHours from 'pages/PPAOnboardingReviewPage/components/WorkingHours';
import DateTimeOverride from 'pages/PPAOnboardingReviewPage/components/DateTimeOverride';
import MinimumTimeAdvance from 'pages/PPAOnboardingReviewPage/components/MinimumTimeAdvance';
import Layout from '../components/Layout';
import { EInvitedOnboardingStep } from '../types';
import { EInvitedProviderStep } from '../steps';
import s from './s.module.less';

const ReviewPage: React.FC = () => {
    const navigate = useNavigate();
    const [basicAPICalling] = useLoadBasicData();
    const [reviewData, loading, loadReviewData] = usePPAOnboardingReviewInfo();
    const [serviceDetail] = useGetPPAServiceDetail();
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        basicAPICalling();
    }, [basicAPICalling]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await getStep();
            const saved = res?.data?.data?.currentStep;
            if (cancelled) return;
            if (saved === EInvitedProviderStep.COMPLETED) {
                navigate(PATH.DASHBOARD, { replace: true });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const handleBack = useCallback(() => {
        navigate(PATH.INVITED_PROVIDER_ONBOARDING_AVAILABILITY, { state: { fromNext: false } });
    }, [navigate]);

    const handleSubmit = useCallback(async () => {
        try {
            setSubmitLoading(true);
            const isIdentificationMissing = !reviewData.identification?.frontUrl;
            const isLicenseMissing =
                !reviewData.licenses?.length || !reviewData.licenses?.some((license) => license.fullUrl);

            if (isIdentificationMissing && isLicenseMissing) {
                message.error(
                    'Government-issued ID and medical license are required. Please upload the documents to submit.',
                );
                return;
            }
            if (isIdentificationMissing) {
                message.error('Government-issued ID is required. Please upload the document to submit.');
                return;
            }
            if (isLicenseMissing) {
                message.error('Medical license is required. Please upload the document to submit.');
                return;
            }

            const submitRes = await submitInvitedProviderReview({
                submitTime: dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
            });
            if (submitRes.error) {
                return;
            }
            await saveStep(EInvitedProviderStep.GO_LIVE);
            navigate(PATH.INVITED_PROVIDER_GO_LIVE);
        } finally {
            setSubmitLoading(false);
        }
    }, [navigate, reviewData]);

    const isLicenseWarning = reviewData.disqualifyReason === 'Medical license expired / invalid';
    const isIdentificationWarning = reviewData.disqualifyReason === 'Government-issued ID expired / invalid';

    return (
        <Layout
            step={EInvitedOnboardingStep.AVAILABILITY}
            aiChatPageId={EAIChatPageId.INVITED_PROVIDER_REVIEW}
            showProcessBar={false}
            showPrefillNotice={false}
            footer={
                <div className={s.btnWrap}>
                    <Button style={{ background: '#fff' }} onClick={handleBack}>
                        Back
                    </Button>
                    <Button type="primary" onClick={handleSubmit} loading={submitLoading}>
                        Submit for review
                    </Button>
                </div>
            }
        >
            <Spin spinning={loading}>
                <div className={s.wrap}>
                    <div className={s.title}>Review your setup before going live</div>
                    <div className={s.desc}>
                        Please confirm your information before moving forward. You can always edit later if something
                        changes.
                    </div>
                    {reviewData.disqualifyReason && (
                        <div className="mb-6">
                            <WarningTips title="Some sections need your attention before you can go live. Please review the highlighted areas below." />
                        </div>
                    )}
                    <div className="flex flex-col mb-6">
                        <ProfileCard initValue={reviewData.basicInfo} onSave={loadReviewData} />
                        <ServicesCard
                            initValue={reviewData.services}
                            specialty={serviceDetail?.selectedSpecialty}
                            onSave={loadReviewData}
                        />
                        <PersonalIndentification
                            initValue={reviewData.identification}
                            onSave={loadReviewData}
                            isWarning={isIdentificationWarning}
                        />
                        <MedicalLicenses
                            initValue={reviewData.licenses}
                            onSave={loadReviewData}
                            isWarning={isLicenseWarning}
                        />
                        <WorkingHours initValue={reviewData.availability} onSave={loadReviewData} />
                        <DateTimeOverride initValue={reviewData.override} onSave={loadReviewData} />
                        <MinimumTimeAdvance initValue={reviewData.calendarSetting} onSave={loadReviewData} />
                    </div>
                </div>
            </Spin>
        </Layout>
    );
};

export default ReviewPage;
